import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ExpansionPanelComponent } from '../expansion-panel/expansion-panel.component';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { EquipmentState, EquipmentDetails } from '../../../store/equipment/equipment.model';
import { clearEquipmentData, storeEquipmentData } from '../../../store/equipment/equipment.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { EquipmentService } from '../../../services/equipment/equipment.service';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppLauncherInfo } from '../../../store/appLauncher/appLauncher.model';
import { map, filter, Observable } from 'rxjs';

class NoResourcesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoResourcesError';
  }
}

class NotValidEquipmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotValidEquipmentError';
  }
}

@Component({
  selector: 'app-equipment-panel',
  standalone: true,
  imports: [ExpansionPanelComponent, CommonModule],
  templateUrl: './equipment-panel.component.html',
  styleUrl: './equipment-panel.component.css'
})
export class EquipmentPanelComponent implements OnInit, OnDestroy {
  equipmentDetails: EquipmentDetails = {
    id: '',
    make: '',
    model: '',
    serialNumberRange: '',
    pvc: '',
    equipmentCode: ''
  };
  wasSuccess$: Observable<boolean>;
  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<{ equipment: EquipmentState }>,
    private store2: Store<{appLauncher: AppLauncherInfo}>,
    private route: ActivatedRoute,
    private equipmentService: EquipmentService,
    private cdr: ChangeDetectorRef,
    private router: Router,
) {
  this.wasSuccess$ = this.store2.select('appLauncher').pipe(
    map(appLauncherInfo => appLauncherInfo.wasSuccess));
  }

  ngOnInit(): void {
    const queryParamSub = this.route.queryParams.subscribe(params => {
      const equipmentCode = params['equipment_code'];
      // Ensure that we only make the call to equipmentDetails if the token was valid
      this.wasSuccess$.subscribe(value => {
        if (equipmentCode && value) {
        this.loadEquipmentData(equipmentCode);
      }});      
    });
    this.subscriptions.push(queryParamSub);
    
    // Subscribe to store updates
    const storeSub = this.store.select(state => state.equipment.equipment.details).subscribe(details => {
      this.equipmentDetails = details || this.equipmentDetails;
      this.cdr.detectChanges();
    });
    this.subscriptions.push(storeSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadEquipmentData(equipmentCode: string): void {
    // Clear existing data first
    this.equipmentDetails = {
      id: '',
      make: '',
      model: '',
      serialNumberRange: '',
      pvc: '',
      equipmentCode: ''
    };
    
    // Fetch equipment details from the API and get formatted resources
    this.equipmentService.getEquipmentDetails(equipmentCode).then(async response => {
      if (!response){
        throw new NotValidEquipmentError('This equipment does not exist in our records')
      }
      if (response) {
        this.store.dispatch(clearEquipmentData());
        if (response.resources.length === 0) {
          throw new NoResourcesError('This equipment has no resources')
        }
        // Get the s3 and download urls of the resources returned inn eqp data call
        const resources = response.resources?.map((resource: { title: string, url: string }) => {
          if (!resource.url.endsWith('.pdf')) {
            return [];
          }
          const fileName = resource.url.split('/').pop()?? '';
          const manualType = resource.url.split('/').slice(-2, -1)[0];
          const documentMake = resource.url.split('/').slice(-3, -2)[0];
          const providerResourceUrl = `${environment.integrations.manualDownload}?equipment_make=${documentMake}&file_name=${encodeURIComponent(fileName)}&manual_type=${manualType}`;
          return {
            title: resource.title,
            url: resource.url,
            downloadUrl: '',
            s3Key: '',
            providerResourceUrl: providerResourceUrl
          };
        }) || [];
        const payload = {
          equipment: {
            details: response.details || this.equipmentDetails,
            resources: resources || [],
            prompts: response.prompts || []
          }
        };
        // Store the fetched data in the store
        this.store.dispatch(storeEquipmentData(payload));
        // Pass payload into searchEquipment function which will get the PDF links
        await this.equipmentService.prepareEquipmentData(payload.equipment);
        // Refresh page for newly loaded data *Potentitally wait to load page till here*
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    }).catch(error => {
      if (error instanceof NoResourcesError) {
        const errorMessage = 'Equipment ' + equipmentCode + ' has no manuals mapped'
        const errorMessage2 = ''
        this.router.navigate(['/technician/error'], { 
          queryParams: { message: errorMessage,
            message2: errorMessage2
            }
        });
      }
      else if(error instanceof NotValidEquipmentError) {
        const errorMessage = 'The Equipment you are searching for does not match our current records.'
        const errorMessage2 = ''
        this.router.navigate(['/technician/error'], { 
          queryParams: { message: errorMessage,
            message2: errorMessage2
            }
        });
      }
    });
  }
}